import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider
} from '@mui/material';
import authService from '../../../api-authorization/AuthorizeService';

const DuplicateTractorConfigDialog = ({
  tractor,
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('template');
  const [tractors, setTractors] = useState([]);
  const [sourceTractorId, setSourceTractorId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [duplicationResult, setDuplicationResult] = useState(null);

  useEffect(() => {
    const fetchTractors = async () => {
      if (!open || !tractor?.id) return;

      const token = await authService.getAccessToken();
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/tractors/all`, {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          setTractors([]);
          setSourceTractorId('');
          return;
        }

        const allTractors = await response.json();
        const sourceOptions = allTractors.filter(t => t.id !== tractor.id);
        setTractors(sourceOptions);
        setSourceTractorId(sourceOptions.length > 0 ? String(sourceOptions[0].id) : '');
      } catch {
        setTractors([]);
        setSourceTractorId('');
      }
    };

    const fetchTemplates = async () => {
      if (!open) return;

      const token = await authService.getAccessToken();
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/tractors/configuration-templates`, {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          setTemplates([]);
          setSelectedTemplateId('');
          return;
        }

        const data = await response.json();
        setTemplates(data);
        setSelectedTemplateId(data.length > 0 ? data[0].id : '');
      } catch {
        setTemplates([]);
        setSelectedTemplateId('');
      }
    };

    if (open && !duplicationResult) {
      fetchTractors();
      fetchTemplates();
    }
  }, [open, tractor, duplicationResult]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'sprayer' && !sourceTractorId) {
      toast.error('Please select a source sprayer.');
      return;
    }
    if (mode === 'template' && !selectedTemplateId) {
      toast.error('Please select a template.');
      return;
    }

    const token = await authService.getAccessToken();
    setLoading(true);

    try {
      const url = mode === 'sprayer'
        ? `${process.env.REACT_APP_BASE_URL}/api/tractors/duplicate-config`
        : `${process.env.REACT_APP_BASE_URL}/api/tractors/apply-template`;

      const body = mode === 'sprayer'
        ? { sourceTractorId: Number(sourceTractorId), targetTractorId: tractor.id }
        : { templateId: selectedTemplateId, targetTractorId: tractor.id };

      const response = await fetch(url, {
        method: 'POST',
        headers: !token
          ? { 'Content-Type': 'application/json' }
          : {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        setDuplicationResult(result);
      } else if (response.status === 400) {
        toast.error('Configuration already exists');
      } else {
        toast.error(`Failed (${response.status} ${response.statusText}).`);
      }
    } catch (err) {
      toast.error('Error: ' + err);
    }

    setLoading(false);
  };

  const handleCloseResults = () => {
    setDuplicationResult(null);
    onSuccess();
  };

  if (duplicationResult) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            Configuration Duplicated Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            <Typography variant="h5" sx={{ pb: 1 }}>
              {tractor?.name}
            </Typography>

            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Booms Created:</TableCell>
                  <TableCell align="right">{duplicationResult.boomsCreated}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nozzles Created:</TableCell>
                  <TableCell align="right">{duplicationResult.nozzlesCreated}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nozzle Profiles Created:</TableCell>
                  <TableCell align="right">{duplicationResult.nozzleProfilesCreated}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Default Nozzle Profile:
              </Typography>
              <Typography variant="body2">
                {duplicationResult.defaultNozzleProfileName || 'None'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseResults}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const isSubmitDisabled = loading ||
    (mode === 'sprayer' && !sourceTractorId) ||
    (mode === 'template' && !selectedTemplateId);

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <EditIcon /> Configure Sprayer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 360 }}>
            <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
              {tractor?.name}
            </Typography>
            <FormControl>
              <FormLabel>Configuration Source</FormLabel>
              <RadioGroup
                row
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                <FormControlLabel value="template" control={<Radio />} label="Add From Template" />
                <FormControlLabel value="sprayer" control={<Radio />} label="Copy from Sprayer" />
              </RadioGroup>
            </FormControl>

            {mode === 'sprayer' && (
              <FormControl fullWidth>
                <FormLabel>Select Source Sprayer</FormLabel>
                <Select
                  value={sourceTractorId}
                  onChange={(e) => setSourceTractorId(e.target.value)}
                  displayEmpty
                >
                  {tractors.length === 0 && (
                    <MenuItem value="">
                      <em>No source sprayers available</em>
                    </MenuItem>
                  )}
                  {tractors.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {mode === 'template' && (
              <FormControl fullWidth>
                <FormLabel>Select Template</FormLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  displayEmpty
                >
                  {templates.length === 0 && (
                    <MenuItem value="">
                      <em>No templates available</em>
                    </MenuItem>
                  )}
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="outlined" disabled={isSubmitDisabled}>
            {mode === 'sprayer' ? 'Duplicate' : 'Apply Template'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DuplicateTractorConfigDialog;